import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { changeChartOptionSelected } from '../../redux/ChartPoperties/ChartPropertiesActions';
import { changeDynamicMeasureOption } from '../../redux/DynamicMeasures/DynamicMeasuresActions';
import ControlDetail from './ControlDetail';
import { RootState } from '../../redux';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Tooltip } from '@mui/material';
import { shouldShowWarning } from './ChartWarningHelper';
import { checkIfMissingColumn } from '../CommonFunctions/CommonFunctions';
import { fontSize } from '../..';

interface ChartControlObjectsProps {
  fromDashboard?: boolean;
  iconColor?: string;
  selectedTabBackgroundColor?: string;
  backgroundColor?: string;
}

const ChartControlObjects: React.FC<ChartControlObjectsProps> = ({
  fromDashboard = false,
  iconColor,
  selectedTabBackgroundColor,
  backgroundColor,
}) => {
  const { chartProp, tabTileProps, dynamicMeasureState, tabState, chartControls } = useSelector(
    (state: RootState) => ({
      chartProp: state.chartProperties,
      tabTileProps: state.tabTileProps,
      dynamicMeasureState: state.dynamicMeasuresState,
      tabState: state.tabState,
      chartControls: state.chartControls,
    }),
  );
  const dispatch = useDispatch();

  const [missingExtraMeasureField, setMissingExtraMeasureField] = useState<boolean>(false);

  const propKey =
    fromDashboard && tabState.selectedTileKey
      ? tabState.selectedTileKey
      : `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;

  const selectedChart = chartProp.properties[propKey]?.chartType;
  const tooltipFormats = chartControls.properties[propKey]?.formatOptions?.tooltipFormats;

  // Check if Dashboard Color Scheme is set to "Auto"
  const isAutoMode = !tabState.tabs[tabTileProps.selectedTabId]?.dashboardState?.tilesTodashboard;

  const selectedDynamicMeasureProps =
    dynamicMeasureState.dynamicMeasureProps?.[`${dynamicMeasureState.selectedTabId}`]?.[
      `${dynamicMeasureState.selectedTileId}`
    ]?.[`${dynamicMeasureState.selectedTileId}.${dynamicMeasureState.selectedDynamicMeasureId}`];

  const richTextOptionList: string[] = ['Format', 'Style', 'Cond.Form'];

  const dashTilesDetails = tabState.tabs[tabTileProps.selectedTabId]?.dashTilesDetails;
  const dashGridSize = tabTileProps.dashGridSize;
  const tileDetail = dashTilesDetails ? dashTilesDetails[propKey] : null;

  const graphDimension = tileDetail
    ? {
        height:
          selectedChart === 'simplecard' || selectedChart === 'wordCloud'
            ? (Number(tileDetail.height) || 0) * (Number(dashGridSize.y) || 0) - 4
            : (Number(tileDetail.height) || 0) * (Number(dashGridSize.y) || 0) - 32,
        width:
          selectedChart === 'simplecard' || selectedChart === 'wordCloud'
            ? (Number(tileDetail.width) || 0) * (Number(dashGridSize.x) || 0) - 4
            : ((Number(tileDetail.width) || 0) * (Number(dashGridSize.x) || 0) * 24) / 25 - 4,
      }
    : null;

  const changeChartOption = (propKey: string, chartOption: string) => {
    dispatch(changeChartOptionSelected(propKey, chartOption));
  };

  const changeDynamicMeasureOptions = (value: string) => {
    dispatch(changeDynamicMeasureOption(value));
  };

  const barOptionsList: string[] = [
    'Title',
    'Dataset',
    'Label',
    'Legend',
    'Margin',
    'Grid',
    'Axis',
    'Tooltip',
    'Color',
    'Sort',
    'Show/Hide',
    'User Level Filter',
    'Bar/Line',
  ];

  const waterfallOptionsList: string[] = [
    'Title',
    'Dataset',
    'Label',
    'Legend',
    'Margin',
    'Grid',
    'Axis',
    'Tooltip',
    'Color',
    'Sort',
    'Show/Hide',
    'Total',
    'User Level Filter',
  ];

  const scatterPlotOptionsList: string[] = [
    'Title',
    'Dataset',
    'Label',
    'Legend',
    'Margin',
    'Grid',
    'Axis',
    'Tooltip',
    'Color',
    'Show/Hide',
    'User Level Filter',
    'Style',
  ];

  const lineOptionsList: string[] = [
    'Title',
    'Dataset',
    'Label',
    'Legend',
    'Margin',
    'Grid',
    'Axis',
    'Tooltip',
    'Color',
    'Sort',
    'Style',
    'Show/Hide',
    'User Level Filter',
    'Bar/Line',
  ];

  const treemapOptionsList: string[] = [
    'Title',
    'Dataset',
    'Label',
    'Legend',
    'Margin',
    'Tooltip',
    'Color',
    'Style',
    'Show/Hide',
    'User Level Filter',
  ];

  const pieOptionsList: string[] = [
    'Title',
    'Dataset',
    'Label',
    'Legend',
    'Margin',
    'Rotation',
    'Tooltip',
    'Color',
    'Sort',
    'Show/Hide',
    'User Level Filter',
  ];

  const wordCloudOptionList: string[] = [
    'Title',
    'Dataset',
    'Tooltip',
    'Ignore words',
    'Style',
    'Color',
  ];

  const funnelOptionList: string[] = [
    'Title',
    'Dataset',
    'Label',
    'Legend',
    'Margin',
    'Tooltip',
    'Color',
    'Format',
    'User Level Filter',
  ];

  const gaugeOptionList: string[] = [
    'Title',
    'Dataset',
    'Label',
    'Margin',
    'Rotation',
    'Tooltip',
    'Color',
    'Format',
    'User Level Filter',
  ];

  const heatmapOptionList: string[] = [
    'Title',
    'Dataset',
    'Legend',
    'Label',
    'Margin',
    'Color',
    'Grid',
    'Axis',
    'Tooltip',
    'Sort',
    'Show/Hide',
    'User Level Filter',
  ];

  const crossTabOptionList: string[] = [
    'Title',
    'Dataset',
    'Style',
    'Color',
    'Format',
    'Sort',
    'Cond.Form',
    'Show/Hide',
    'Layout',
    'User Level Filter',
  ];

  const boxPlotOptionsList: string[] = [
    'Title',
    'Dataset',
    'Legend',
    'Label',
    'Tooltip',
    'Margin',
    'Color',
    'Grid',
    'Axis',
    'Format',
    'Sort',
    'Show/Hide',
    'User Level Filter',
  ];

  const calendarOptionList: string[] = [
    'Title',
    'Dataset',
    'Legend',
    'Label',
    'Margin',
    'Tooltip',
    'Color',
    'Style',
    'Sort',
    'Show/Hide',
    'User Level Filter',
  ];

  const SankeyOptionList: string[] = [
    'Title',
    'Dataset',
    'Label',
    'Margin',
    'Tooltip',
    'Color',
    'Style',
    'Sort',
    'Show/Hide',
    'User Level Filter',
  ];

  const simpleCardOptionList: string[] = [
    'Label',
    'Dataset',
    'Color',
    'Format',
    'Style',
    'Cond.Form',
    'User Level Filter',
  ];

  const tableOptionList: string[] = [
    'Title',
    'Dataset',
    'Style',
    'Color',
    'Format',
    'Sort',
    'Cond.Form',
    'Show/Hide',
    'Layout',
    'User Level Filter',
  ];

  const filledMapOptionList: string[] = [
    'Title',
    'Dataset',
    'Label',
    'Tooltip',
    'Color',
    'Style',
    'Show/Hide',
    'User Level Filter',
    'Visual Scale',
  ];

  const bubbleMapOptionList: string[] = [
    'Title',
    'Dataset',
    'Label',
    'Tooltip',
    'Color',
    'Style',
    'Show/Hide',
    'User Level Filter',
    'Visual Scale',
  ];

  useEffect(() => {
    if (selectedChart === 'simplecard') {
      changeChartOption(propKey, 'Label');
    } else {
      changeChartOption(propKey, 'Title');
    }
  }, [chartProp.properties[propKey]?.chartType, propKey]);

  useEffect(() => {
    (async () => {
      if (tooltipFormats?.extraMeasureField?.[0]?.fields?.length > 0) {
        const promises = tooltipFormats.extraMeasureField[0]?.fields.map(async (field: any) => {
          const res = await checkIfMissingColumn(
            field,
            chartProp.properties[propKey]?.selectedDs,
            tabTileProps.tablesForSelectedDataSets[chartProp.properties[propKey]?.selectedDs.id],
            chartControls.properties[propKey]?.chartErrorDetails?.statusCode === 400 &&
              chartProp.properties[propKey]?.oldSelectedDs?.id !==
                chartProp.properties[propKey]?.selectedDs.id,
          );
          const showWarning =
            chartControls.properties[propKey]?.chartErrorDetails?.statusCode === 400 &&
            chartProp.properties[propKey].oldSelectedDs?.id !==
              chartProp.properties[propKey]?.selectedDs.id
              ? Boolean(!res.newTableId)
              : Boolean(res.isMissing);
          return showWarning;
        });
        const results = await Promise.all(promises);
        const anyMissing = results.some(Boolean);
        setMissingExtraMeasureField(anyMissing);
      } else setMissingExtraMeasureField(false);
    })();
  }, [
    chartControls.properties[propKey]?.chartErrorDetails,
    tooltipFormats?.extraMeasureField?.[0]?.fields?.length,
    chartProp.properties[propKey]?.oldSelectedDs?.id,
    chartProp.properties[propKey]?.selectedDs?.id,
  ]);

  const renderOption = (option: string) => {
    // Determine if this option should be disabled when viewed from the Dashboard
    const isDisabled =
      fromDashboard &&
      (option === 'Tooltip' ||
        option === 'User Level Filter' ||
        (isAutoMode && option === 'Color'));

    const isSelected = chartProp.properties[propKey]?.chartOptionSelected === option;

    // Base styles
    let dynamicStyles: React.CSSProperties = { position: 'relative' };

    if (isDisabled) {
      dynamicStyles = { ...dynamicStyles, opacity: 0.5, cursor: 'not-allowed' };
    }

    if (fromDashboard && iconColor && selectedTabBackgroundColor) {
      dynamicStyles = {
        ...dynamicStyles,
        color: iconColor,
        backgroundColor: isSelected ? selectedTabBackgroundColor : 'transparent',
        border: `2px solid ${isSelected ? selectedTabBackgroundColor : 'rgba(224, 224, 224, 1)'}`,
        fontWeight: isSelected ? 600 : 400,
        transition: 'all 0.2s ease',
      };
    }

    const tooltipTitle = isDisabled
      ? isAutoMode && option === 'Color'
        ? 'Color Scheme is set to Auto in dashboard. Switch to Manual to change chart colors.'
        : `The ${option} property cannot be modified directly from the dashboard.`
      : '';

    const showMissingWarningIcon = option === 'Tooltip' && missingExtraMeasureField;

    const content = (
      <div
        key={option}
        className={isSelected ? 'optionImageSelected' : 'optionImage'}
        style={dynamicStyles}
        onClick={(e) => {
          if (isDisabled) {
            e.preventDefault();
            return;
          }
          changeChartOption(propKey, option);
        }}
      >
        {option}

        {fromDashboard &&
          shouldShowWarning(
            option,
            selectedChart,
            graphDimension,
            chartControls.properties[propKey],
          ) && (
            <Tooltip
              title={`${option} is hidden because the chart size is too small in the dashboard. Try increasing the chart height or width.`}
              arrow
              placement="top"
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: '12px',
                    whiteSpace: 'pre-line',
                    maxWidth: '250px',
                  },
                },
              }}
            >
              <WarningAmberIcon
                style={{
                  fontSize: '14px',
                  color: '#ff9800',
                  marginLeft: '4px',
                  verticalAlign: 'middle',
                }}
              />
            </Tooltip>
          )}

        {showMissingWarningIcon && (
          <Tooltip arrow placement="top" title={'The chart has missing tooltip fields'}>
            <WarningAmberIcon
              style={{
                color: '#850000ff',
                fontSize: fontSize?.large || '16px',
                position: 'absolute',
                transform: 'translateY(-50%)',
                top: '50%',
                right: '10%',
              }}
            />
          </Tooltip>
        )}
      </div>
    );

    if (isDisabled) {
      return (
        <Tooltip
          title={tooltipTitle}
          arrow
          placement="top"
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '12px',
                whiteSpace: 'pre-line',
                maxWidth: '250px',
              },
            },
          }}
        >
          {content}
        </Tooltip>
      );
    }

    return content;
  };

  const RenderOptions: any = () => {
    switch (selectedChart) {
      case 'multibar':
      case 'horizontalBar':
        return barOptionsList.map(renderOption);
      case 'stackedBar':
      case 'horizontalStacked':
        return barOptionsList.slice(0, barOptionsList.length - 1).map(renderOption);
      case 'waterfall':
        return waterfallOptionsList.map(renderOption);
      case 'scatterPlot':
        return scatterPlotOptionsList.map(renderOption);
      case 'line':
      case 'area':
        return lineOptionsList.map(renderOption);
      case 'stackedArea':
        return lineOptionsList.slice(0, lineOptionsList.length - 1).map(renderOption);
      case 'calendar':
        return calendarOptionList.map(renderOption);
      case 'sankey':
        return SankeyOptionList.map(renderOption);
      case 'pie':
      case 'donut':
      case 'rose':
        return pieOptionsList.map(renderOption);
      case 'wordCloud':
        return wordCloudOptionList.map(renderOption);
      case 'boxPlot':
        return boxPlotOptionsList.map(renderOption);
      case 'funnel':
        return funnelOptionList.map(renderOption);
      case 'gauge':
        return gaugeOptionList.map(renderOption);
      case 'heatmap':
        return heatmapOptionList.map(renderOption);
      case 'treeMap':
        return treemapOptionsList.map(renderOption);
      case 'table':
        return tableOptionList.map(renderOption);
      case 'crossTab':
        return crossTabOptionList.map(renderOption);
      case 'filledMap':
        return filledMapOptionList.map(renderOption);
      case 'bubbleMap':
        return bubbleMapOptionList.map(renderOption);
      case 'richText':
        return richTextOptionList.map((option: any, i: number) => {
          return (
            <div
              key={option}
              className={
                selectedDynamicMeasureProps?.chartOptionSelected === option
                  ? 'optionImageSelected'
                  : 'optionImage'
              }
              style={{
                textAlign: 'center',
                gridColumn: i === 2 ? '1/span 3' : 'auto',
              }}
              onClick={() => changeDynamicMeasureOptions(option)}
            >
              {option}
            </div>
          );
        });
      case 'simplecard':
        return simpleCardOptionList.map(renderOption);

      default:
        return <span> under construction</span>;
    }
  };

  if (fromDashboard && selectedChart === 'richText') {
    return (
      <div
        style={{
          textAlign: 'center',
          marginTop: '.5rem',
          fontSize: '12px',
          color: iconColor,
          padding: '0 10px',
        }}
      >
        Chart controls for Rich Text are not available in dashboard.
      </div>
    );
  }

  return (
    <>
      <div>
        <div className="chartOptionImagesContainer">
          <RenderOptions />
        </div>
        <ControlDetail
          fromDashboard={fromDashboard}
          iconColor={iconColor}
          backgroundColor={backgroundColor}
        />
      </div>
    </>
  );
};

export default ChartControlObjects;
